import { Fragment, PureComponent, ReactNode, useEffect } from 'react';
import Select, {
    components,
    ControlProps,
    ValueContainerProps,
    OptionProps,
    MenuProps,
    OptionsType,
    StylesConfig,
} from 'react-select';
import { GroupHeadingProps } from 'react-select/src/components/Group';
import debounce from 'lodash/debounce';
import { CSSObject } from '@emotion/serialize';

import { fetchMatchingDrugsLabTests } from '../../../../api';
import {
    DEFAULT_GENIE_MEMORY_CONFIG,
    LOCAL_ID_PREFIX,
    MED_TABLE_COL_QUANTITY_KEY,
} from '../../../../constants';
import { GInjection, GLabTest, GMedication } from '../../../../store/db';
import { getItemFromMisc, isIDristi } from '../../../../store/idbHandler';
import {
    Drug as MatchingDrug,
    LabTest,
    DRUG_DOSAGE_DURATION_TIMING_ENUM,
    S_TYPE,
    Drug,
} from '../../../../api/fetchMatchingDrugsLabTests';
import {
    Frequency,
    MedicationsEntity,
    LabTestsEntityOrProceduresEntity,
    InjectionsEntity,
    Prescription,
} from '../../../../store/types/prescription';
import { filterSuggestion, getUnit, hashCode } from '../../../../store/utils';
import { PAD_V2_CONTAINER } from './PadContainer';
import { connect } from 'react-redux';
import { GlobalState } from '../../../../store/reducers';
import {
    getComplimentaryDrug,
    getDoctorPackages,
    getDrugAllergies,
    getLabTests,
    getMedications,
    getRxClinicId,
    getSuggestedDrugs,
    getSuggestedLabTests,
    getPrescription,
} from '../../../../store/selectors/prescriptionPad';
import onContainerClick from '../../../../pages/prescriptionPad/onClickContainer';
import {
    API_SEARCH,
    AS_EMPTY,
    AS_OPTIONS_BOOKABLE_PACKAGES,
    AS_OPTIONS_FREQUENCTLY,
    AS_OPTIONS_SEARCH,
    AS_OPTIONS_SUGGESTED,
    AS_OPTIONS_TESTS_AND_PANELS,
    AS_SEARCH,
    COPILOT,
    CUSTOM,
    GENIE,
    INJECTIONS_KIND,
    LAB_TESTS_KIND,
    LIST_LABEL_TYPE,
    MEDICINES_KIND,
    PxElSourceTrack,
    SUGGESTED,
} from '../../trackSource';
import { track } from '../../../../tracking';
import { capsulesIcon, escTextIcon, labsIcon, labsPackageIcon } from '../../../common/Icons';
import { findInventoryStatus } from './padV2/MedTableRenderer';
import {
    fetchDrugsLabTestsStatus,
    getDrugAllergy,
    getTestsAndPackagesFromOptions,
    getTopSuggestionsForLabs,
    isOnboardedForLab,
    isOnboardedForPharma,
    isRXMonetisationT3,
    isRXMonetisationT4,
    isHXNGVendorPackageOrDoctorPackage,
} from '../../../../pages/utils';
import { getGenieSetting, getMedTypeIds } from '../../../../store/selectors/session';
import { CodedMedications } from '../../../../api/fetchDrugSearch';
import AddDrugDialogWrapper from './AddDrugDialogWrapper';
import { HXNG_MAPPED, HXNG_STATUS } from '../../../../api/fetchDrugsAndLabTestsStatus';
import { Popover } from 'antd';
import { handleKeyDownEvent } from './handleKeyDownEvent';
import { DoctorPackages } from '../../../../api/fetchDoctorPackages';
import { TestingType } from '../finishScreen/BookLabTestsUtils';
import PadGenieAutoSuggestFuseManager, {
    INJECTIONS,
    LAB_TESTS,
    MEDICATIONS,
} from '../../PadGenieAutoSuggestFuseManager';
import { GenieMemoryConfig, GenieMemoryItem, GenieSettings } from '../../../../api/pushGenie';
import { getMActiveId } from '../../../../store/localstorage';
import predictNextMedicines from '../../../../api/predictNextMedicines';
import fetchDrugsByIds from '../../../../api/fetchDrugsByIds';

export type Kind = typeof MEDICINES_KIND | typeof LAB_TESTS_KIND | typeof INJECTIONS_KIND;

export type ItemType = (MatchingDrug | LabTest | GMedication | GLabTest | GInjection) &
    PxElSourceTrack & { kind: Kind };

type CompProps = {
    kind: Kind;
    onSelect: (
        drug: MedicationsEntity | LabTestsEntityOrProceduresEntity | InjectionsEntity,
    ) => void;
    prescriptionElements?: E[];
};

type E = {
    id: string;
    name: string;
};

export interface InventoryItem {
    mdb_id: string;
    quantity: number;
    mrp: number;
    sku: string;
    in_stock: boolean;
    evitalId: string;
}

export interface InventoryV2 {
    [key: string]: Inventory;
}

export interface Inventory {
    [id: string]: InventoryItem;
}

interface Props extends CompProps {
    elements: E[];
    suggestedElements: E[];
    drugAllergies?: Set<string>;
    medTypeIds?: string[];
    isForAddMedicines?: boolean;
    hideVendorPackages?: boolean;
    hideCustomEntity?: boolean;
    doctorPackages?: DoctorPackages;
    selectedClinic?: string;
    className?: string;
    isForHXNGFlow?: boolean;
    disableAutoScrollOnSelect?: boolean;
    prescription?: Prescription;
    genieMemoryConfig?: GenieMemoryConfig;
}

export const FORM_ABBR: { [k: string]: string } = {
    tablet: 'Tab',
    capsule: 'Cap',
    'capsule pr': 'Cap',
    'capsule mr': 'Cap',
    injection: 'Inj',
    drop: 'Drp',
    inhaler: 'Inh',
    infusion: 'Inf',
    gel: 'Gel',
    kit: 'Kit',
    sachet: 'Sac',
    syrup: 'Syr',
};

export const medicinesInventoryStatusOptions = {
    inStock: {
        iconColor: 'fill-darwin-accent-green-900',
        bg: 'bg-darwin-accent-green-100',
        hoverClass: 'hover:bg-darwin-accent-green-900',
        text: 'in Stock',
        textColor: 'text-darwin-accent-green-900',
        borderColor: 'border-darwin-accent-green-500',
        circleDropdown: 'circle-inventory-in-stock',
        dropdownItem: 'inventory-in-stock-item',
        textFoxHxng: 'Available',
        textFoxLabs: 'Bookable',
    },
    lessInStock: {
        iconColor: 'fill-darwin-accent-bright-yellow-900',
        bg: 'bg-darwin-accent-investigations-ochre-100',
        hoverClass: 'hover:bg-darwin-accent-bright-yellow-900',
        text: 'in Stock',
        textColor: 'text-darwin-accent-bright-yellow-900',
        borderColor: 'border-darwin-accent-investigations-ochre-500',
        circleDropdown: 'circle-inventory-less-in-stock',
        dropdownItem: 'inventory-less-in-stock-item',
        textFoxHxng: 'Available',
        textFoxLabs: 'Bookable',
    },
    outOfStock: {
        iconColor: 'fill-darwin-status-warning-500',
        bg: 'bg-darwin-status-warning-100',
        hoverClass: 'hover:bg-darwin-status-warning-500',
        text: 'Out of Stock',
        textColor: 'text-darwin-status-warning-500',
        borderColor: 'border-darwin-status-warning-300',
        circleDropdown: 'circle-inventory-out-of-stock',
        dropdownItem: 'inventory-out-of-stock-item',
        textFoxHxng: 'Price unavailable',
        textFoxLabs: 'Not bookable',
    },
};

const DropdownIndicator = null;

const orderMedicinesStatusText = (status?: string): JSX.Element | null => {
    if (!status) {
        return null;
    }
    return (
        <div className="flex items-center space-x-4">
            <div
                className={`flex justify-center items-center inventory-items ${medicinesInventoryStatusOptions.outOfStock.bg} cursor-pointer h-20 w-20 rounded-full ${medicinesInventoryStatusOptions.outOfStock.hoverClass}`}
            >
                {capsulesIcon(medicinesInventoryStatusOptions.outOfStock.iconColor)}
            </div>
            <div
                className={`px-4 py-2 rounded-4 ${medicinesInventoryStatusOptions.outOfStock.bg}`}
                style={{ width: 'fit-content' }}
            >
                <p
                    className={`font-700 text-12 ${medicinesInventoryStatusOptions.outOfStock.textColor} capitalize`}
                >
                    {status.split('_').join(' ')}
                </p>
            </div>
        </div>
    );
};

const optionHtml = (
    data: ItemType,
    isFocussed: boolean,
    isAllered: boolean,
    isDrugStatusUnavailable?: string,
    isLabTestBookable?: boolean,
) => {
    const {
        kind,
        id,
        name,
        track,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        inventory: apiInventory,
    } = data;
    const inventoryItem =
        'inventory_status' in data && data.inventory_status
            ? {
                  mdb_id: data.id,
                  quantity: data.inventory_status.quantity,
                  mrp: data.inventory_status.price,
                  sku: (data as MatchingDrug).product_sku || '',
                  in_stock: data.inventory_status.in_stock,
                  evitalId: data.inventory_status.evital_id,
              }
            : undefined;

    const medicineInventoryStatus = inventoryItem && findInventoryStatus(inventoryItem);
    let dropDownClassess = medicineInventoryStatus
        ? medicinesInventoryStatusOptions[medicineInventoryStatus]
        : undefined;

    if (isIDristi() && apiInventory) {
        dropDownClassess = apiInventory.in_stock
            ? medicinesInventoryStatusOptions.inStock
            : medicinesInventoryStatusOptions.outOfStock;
    }

    const isAddNewEntity = id.startsWith(LOCAL_ID_PREFIX) && track?.source !== GENIE;

    const labTestsIncludedInPackage = (data as LabTest)?.content;
    const vendorNames = (data as LabTest)?.vendor_names;

    return (
        <div
            key={id}
            id={id}
            className={`flex items-center justify-between cursor-pointer px-8 ${
                isFocussed
                    ? 'focus-state-dropdown-item'
                    : dropDownClassess?.dropdownItem || 'mx-dropdown-item'
            }`}
        >
            <div className="flex items-start py-4 space-x-8 ">
                {isAddNewEntity ? (
                    <div className="flex items-center w-16 h-16 justify-center m-4">
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M13.5 7C13.5 7.4375 13.1562 7.75 12.75 7.75L7.75 7.75L7.75 12.75C7.75 13.1875 7.40625 13.5313 7 13.5313C6.5625 13.5313 6.25 13.1875 6.25 12.75L6.25 7.75H1.25C0.8125 7.75 0.5 7.4375 0.5 7.03125C0.5 6.59375 0.8125 6.25 1.25 6.25H6.25V1.25C6.25 0.84375 6.5625 0.53125 7 0.53125C7.40625 0.53125 7.75 0.84375 7.75 1.25V6.25L12.75 6.25C13.1562 6.25 13.5 6.59375 13.5 7Z"
                                fill="#004BA8"
                            />
                        </svg>
                    </div>
                ) : (
                    <div
                        className={
                            kind === MEDICINES_KIND || kind === INJECTIONS_KIND
                                ? dropDownClassess?.circleDropdown || 'circle-mx'
                                : 'circle-lx'
                        }
                    >
                        <p>
                            {kind === MEDICINES_KIND
                                ? dropDownClassess
                                    ? capsulesIcon(`${dropDownClassess?.iconColor} w-16 h-16`)
                                    : FORM_ABBR[
                                          ('dosage' in data
                                              ? data.dosage?.dosage_form || ''
                                              : ''
                                          ).toLowerCase()
                                      ] || 'Rx'
                                : kind === LAB_TESTS_KIND &&
                                  isHXNGVendorPackageOrDoctorPackage(data)
                                ? labsPackageIcon('fill-darwin-accent-investigations-ochre-800')
                                : kind === INJECTIONS_KIND
                                ? 'Inj'
                                : 'Lab'}
                        </p>
                    </div>
                )}
                <div className="space-y-4 pt-2">
                    {isAddNewEntity ? (
                        <span className="text-14 text-darwin-primary-500">
                            Add <span className="font-700">" {name} "</span> as a{' '}
                            {kind === MEDICINES_KIND
                                ? 'drug'
                                : kind === INJECTIONS_KIND
                                ? 'injection'
                                : 'lab test'}{' '}
                            {isAllered ? '🔺' : ''}
                        </span>
                    ) : (
                        <p className="flex flex-wrap gap-5 text-neutral-1 text-14">
                            <span>{name}</span>
                            <TestingType i={data} className="px-8 py-3 relative -top-1 text-12" />
                            <span>{isAllered ? '🔺' : ''} </span>
                            {orderMedicinesStatusText(isDrugStatusUnavailable)}
                            {vendorNames?.map((vendor) => (
                                <span className="border border-darwin-neutral-200 rounded-4 px-4 py-3 text-darwin-neutral-700 text-10 bg-white h-max">
                                    {vendor}
                                </span>
                            ))}
                        </p>
                    )}
                    {'generic_name' in data && data.generic_name && (
                        <div className="uppercase text-11 text-darwin-neutral-600">
                            {data.generic_name}
                        </div>
                    )}
                    {labTestsIncludedInPackage?.length ? (
                        <div className="flex items-center space-x-12">
                            <p className="text-12 text-darwin-neutral-700 truncate-overflow-1">
                                {labTestsIncludedInPackage.join(', ')}
                            </p>
                            <LabTestsIncludedInPackage
                                labTestsIncludedInPackage={labTestsIncludedInPackage}
                            />
                        </div>
                    ) : null}
                </div>
                {isLabTestBookable && (
                    <div
                        className={`border border-darwin-accent-green-500 bg-darwin-accent-green-100 rounded-4 p-4`}
                    >
                        <p className={`text-darwin-accent-green-900 font-700 text-12`}>Bookable</p>
                    </div>
                )}
                {inventoryItem && (
                    <div
                        className={`border ${dropDownClassess?.borderColor}  ${dropDownClassess?.bg} rounded-4 p-4`}
                    >
                        <p className={`${dropDownClassess?.textColor} font-700 text-12`}>
                            {`${inventoryItem.in_stock ? inventoryItem?.quantity : ''} ${
                                dropDownClassess?.text
                            }`.trim()}
                        </p>
                    </div>
                )}
                {isIDristi() && apiInventory && (
                    <div
                        className={`border ${dropDownClassess?.borderColor}  ${dropDownClassess?.bg} rounded-4 p-4`}
                    >
                        <p className={`${dropDownClassess?.textColor} font-700 text-12`}>
                            {`${apiInventory.in_stock ? apiInventory?.quantity : ''} ${
                                dropDownClassess?.text
                            }`.trim()}
                        </p>
                    </div>
                )}
            </div>
            <div className="flex items-center">
                {inventoryItem && (
                    <>
                        <p className="text-darwin-neutral-800 text-14 font-400">
                            {inventoryItem?.sku}
                        </p>
                        {inventoryItem?.mrp > 0 && inventoryItem?.sku && (
                            <p className="text-darwin-neutral-400 font-400 text-12 ml-8">|</p>
                        )}
                        {inventoryItem?.mrp > 0 && (
                            <p className="text-darwin-neutral-1000 text-14 font-700 ml-8">
                                MRP. {inventoryItem?.mrp}
                            </p>
                        )}
                    </>
                )}
                {isIDristi() && apiInventory && (
                    <>
                        {'price' in apiInventory && (
                            <p className="text-darwin-neutral-1000 text-14 font-700 ml-8">
                                MRP. ₹ {apiInventory?.price}
                            </p>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

const LabTestsIncludedInPackage: React.FC<{
    labTestsIncludedInPackage?: string[];
}> = ({ labTestsIncludedInPackage }) => {
    return labTestsIncludedInPackage?.length ? (
        <Popover
            placement="right"
            title={
                <p className="font-700 select-none">
                    Tests included in this package ({labTestsIncludedInPackage.length})
                </p>
            }
            content={
                <div className="space-y-8 select-none overflow-auto max-h-328">
                    <div className="space-y-4">
                        {labTestsIncludedInPackage.map((test, index) => (
                            <p className="flex items-start space-x-8">
                                <p>{index + 1}.</p>
                                <p>{test}</p>
                            </p>
                        ))}
                    </div>
                </div>
            }
            trigger="hover"
            overlayClassName="max-w-328"
        >
            <div>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-16 h-16 fill-darwin-primary-500"
                    viewBox="0 0 12 13"
                >
                    <path d="M6 0.5C2.67188 0.5 0 3.19531 0 6.5C0 9.82812 2.67188 12.5 6 12.5C9.30469 12.5 12 9.82812 12 6.5C12 3.19531 9.30469 0.5 6 0.5ZM6 11.375C3.30469 11.375 1.125 9.19531 1.125 6.5C1.125 3.82812 3.30469 1.625 6 1.625C8.67188 1.625 10.875 3.82812 10.875 6.5C10.875 9.19531 8.67188 11.375 6 11.375ZM6.9375 8.375H6.5625V6.3125C6.5625 6.00781 6.30469 5.75 6 5.75H5.25C4.92188 5.75 4.6875 6.00781 4.6875 6.3125C4.6875 6.64062 4.92188 6.875 5.25 6.875H5.4375V8.375H5.0625C4.73438 8.375 4.5 8.63281 4.5 8.9375C4.5 9.26562 4.73438 9.5 5.0625 9.5H6.9375C7.24219 9.5 7.5 9.26562 7.5 8.9375C7.5 8.63281 7.24219 8.375 6.9375 8.375ZM6 5C6.39844 5 6.75 4.67188 6.75 4.25C6.75 3.85156 6.39844 3.5 6 3.5C5.57812 3.5 5.25 3.85156 5.25 4.25C5.25 4.67188 5.57812 5 6 5Z" />
                </svg>
            </div>
        </Popover>
    ) : null;
};

const Option = (props: OptionProps<ItemType, true>) => {
    const { data, selectProps } = props;
    const { focussedItemId, setFocussedId, drugAllergies, drugsLabTestsStatusMap } = selectProps;

    useEffect(() => {
        if (props.isFocused) {
            setFocussedId(data.id);
        }
    }, [props.isFocused]);

    const isFocussed = focussedItemId === data.id;

    const { id, kind } = data;
    const isDrugStatusUnavailable =
        isOnboardedForPharma() &&
        kind === MEDICINES_KIND &&
        (drugsLabTestsStatusMap[id]?.hxng_status &&
        drugsLabTestsStatusMap[id]?.hxng_status !== 'available'
            ? drugsLabTestsStatusMap[id]?.hxng_status
            : data?.hxng_status && data?.hxng_status !== 'available'
            ? data.hxng_status
            : '');
    const isLabTestBookable =
        isOnboardedForLab() &&
        kind === LAB_TESTS_KIND &&
        !isHXNGVendorPackageOrDoctorPackage(data) &&
        ((data as LabTest).book || drugsLabTestsStatusMap[id]?.book);
    const newData = { ...data, ...drugsLabTestsStatusMap[id] };
    return (
        <components.Option {...props} isFocused={isFocussed}>
            {optionHtml(
                newData,
                isFocussed,
                getDrugAllergy(drugAllergies, data),
                isDrugStatusUnavailable,
                isLabTestBookable,
            )}
        </components.Option>
    );
};

export const PRESCRIBE_V2_AS_INPUT = 'PRESCRIBE_V2_AS_INPUT';

// TODO
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const Input = (props) => {
    const { innerRef, selectProps, onChange, onFocus, value } = props;
    const { placeholder, setMenuOnOff, kind } = selectProps;

    const onBBlur = (): void => {
        setMenuOnOff(false);
        onContainerClick();
    };

    const onFFocus = (): void => {
        setMenuOnOff(true);
        onFocus();
    };

    return (
        <div className="flex items-center justify-between w-full bg-transparent">
            <div className="flex items-center space-x-12 w-full">
                <svg
                    className="w-16 h-16 fill-darwin-neutral-400"
                    role="img"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                >
                    <path d="M504.1 471l-134-134C399.1 301.5 415.1 256.8 415.1 208c0-114.9-93.13-208-208-208S-.0002 93.13-.0002 208S93.12 416 207.1 416c48.79 0 93.55-16.91 129-45.04l134 134C475.7 509.7 481.9 512 488 512s12.28-2.344 16.97-7.031C514.3 495.6 514.3 480.4 504.1 471zM48 208c0-88.22 71.78-160 160-160s160 71.78 160 160s-71.78 160-160 160S48 296.2 48 208z"></path>
                </svg>
                <input
                    id={`${PRESCRIBE_V2_AS_INPUT}-${kind}`}
                    ref={innerRef}
                    type="text"
                    className="w-full text-14 placeholder-darwin-neutral-400 text-neutral-black py-10 bg-transparent"
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    onFocus={onFFocus}
                    onBlur={onBBlur}
                    autoComplete="off"
                />
            </div>
        </div>
    );
};

const ValueContainer = (props: ValueContainerProps<ItemType, true>) => {
    const { children } = props;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const child = children[children.length - 1];

    return child;
};

const Control = (props: ControlProps<ItemType, true>) => {
    const { children } = props;
    return <Fragment>{children}</Fragment>;
};

const LoadingIndicator = null;

const Menu = (props: MenuProps<ItemType, true>) => {
    const { children, options } = props;

    if (
        // !inputValue ||
        // TODO check if this can be improved
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        (options[0]?.options?.length || 0) === 0
    ) {
        return null;
    }

    return (
        <components.Menu {...props}>
            <div>{children}</div>
        </components.Menu>
    );
};

const GroupHeading = (
    props: GroupHeadingProps<{ label: LIST_LABEL_TYPE; options: ItemType[] }, true>,
) => {
    const { children, selectProps } = props;
    const { options, kind } = selectProps;
    const escIcon = escTextIcon('h-20 w-20');

    if (children === AS_OPTIONS_SEARCH || !options) {
        return null;
    }

    return (
        <div className="flex items-center justify-between px-8 pb-4">
            {children === AS_OPTIONS_FREQUENCTLY ? (
                <div className="flex items-center space-x-4">
                    <div className="bg-darwin-accent-bright-blue-800 px-6 py-4 caption-1 bold font-700 text-white rounded-4">
                        frequently
                    </div>
                    <p className="text-neutral-6 text-12 font-700">prescribed by you</p>
                </div>
            ) : children === AS_OPTIONS_SUGGESTED ? (
                <div className="flex items-center space-x-4">
                    <div className="bg-darwin-accent-sulphur-green-500 px-6 py-4 caption-1 bold text-white rounded-4">
                        suggested
                    </div>
                    <p className="text-neutral-6 text-12 font-700">
                        for Symptoms / Diagnosis Entered
                    </p>
                </div>
            ) : children === AS_OPTIONS_TESTS_AND_PANELS ? (
                <div className="flex items-center space-x-4">
                    <div className="bg-darwin-accent-bright-blue-800 px-6 py-4 caption-1 bold font-700 text-white rounded-4">
                        tests &amp; panels
                    </div>
                    <p className="text-neutral-6 text-12 font-700">suggested / prescribed by you</p>
                </div>
            ) : children === AS_OPTIONS_BOOKABLE_PACKAGES ? (
                <div className="flex items-center space-x-4">
                    <div className="bg-darwin-accent-bright-blue-800 px-6 py-4 caption-1 bold font-700 text-white rounded-4">
                        bookable packages
                    </div>
                    <p className="text-neutral-6 text-12 font-700">From partner vendors</p>
                </div>
            ) : (
                <div />
            )}

            {options && options.length > 0 && options[options.length - 1].label === children && (
                <div className="flex items-center space-x-8">
                    {isOnboardedForPharma() && kind === MEDICINES_KIND && (
                        <div className="flex items-center space-x-5">
                            <div
                                className={`flex justify-center items-center inventory-items ${medicinesInventoryStatusOptions.inStock.bg} cursor-pointer h-20 w-20 rounded-full ${medicinesInventoryStatusOptions.inStock.hoverClass}`}
                            >
                                {capsulesIcon(medicinesInventoryStatusOptions.inStock.iconColor)}
                            </div>

                            <div
                                className={` px-6 py-3 rounded-4 ${medicinesInventoryStatusOptions.inStock.bg}`}
                                style={{ width: 'fit-content' }}
                            >
                                <p
                                    className={`font-700 text-12 ${medicinesInventoryStatusOptions.inStock.textColor}`}
                                >
                                    {medicinesInventoryStatusOptions.inStock.textFoxHxng}
                                </p>
                            </div>
                        </div>
                    )}

                    {isOnboardedForLab() && kind === LAB_TESTS_KIND && (
                        <div className="flex items-center space-x-5">
                            <div
                                className={`flex justify-center items-center inventory-items ${medicinesInventoryStatusOptions.inStock.bg} cursor-pointer h-20 w-20 rounded-full ${medicinesInventoryStatusOptions.inStock.hoverClass}`}
                            >
                                {labsIcon(medicinesInventoryStatusOptions.inStock.iconColor)}
                            </div>

                            <div
                                className={` px-6 py-3 rounded-4 ${medicinesInventoryStatusOptions.inStock.bg}`}
                                style={{ width: 'fit-content' }}
                            >
                                <p
                                    className={`font-700 text-12 ${medicinesInventoryStatusOptions.inStock.textColor}`}
                                >
                                    {medicinesInventoryStatusOptions.inStock.textFoxLabs}
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="flex items-center space-x-4">
                        {escIcon}
                        <p className="text-neutral-1 body-3">Close</p>
                    </div>
                </div>
            )}
        </div>
    );
};

// TODO
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const SelectContainer = (props) => (
    <components.SelectContainer {...props}>
        <div className="relative">{props.children}</div>
    </components.SelectContainer>
);

const NoOptionsMessage = () => null;
const LoadingMessage = () => null;

// TODO
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const MenuList = (props) => {
    return (
        <components.MenuList
            {...props}
            className="absolute flex z-10 w-full px-8 pt-12 overflow-auto bg-white top-40 shadow-tight-3 rounded-8 space-y-4 mx-dropdown"
        >
            {props.children}
        </components.MenuList>
    );
};

const customComponents = {
    DropdownIndicator,
    ValueContainer,
    Input,
    Control,
    Menu,
    MenuList,
    GroupHeading,
    Option,
    LoadingIndicator,
    NoOptionsMessage,
    LoadingMessage,
    SelectContainer,
};

const customStyles: StylesConfig<ItemType, true> = {
    option: (base) => {
        return {
            ...base,
            padding: 0,
        } as CSSObject;
    },
    menuList: () => {
        return {
            overflow: 'auto',
            maxHeight: 'calc(100vh - 180px)',
        } as CSSObject;
    },
    group: (base) => {
        return {
            ...base,
            paddingTop: 0,
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
        } as CSSObject;
    },
    container: () => {
        return { width: '100%' } as CSSObject;
    },
    menu: () => {
        return {} as CSSObject;
    },
};

type Options = {
    label: LIST_LABEL_TYPE;
    options: ItemType[];
}[];

export type DrugsLabTestsStatusMap = {
    [k: string]: {
        hxng_mapped?: HXNG_MAPPED;
        hxng_status?: HXNG_STATUS;
        book?: boolean;
    };
};

type State = {
    isMenuOpen: boolean;
    options: Options;
    inputValue: string;
    focussedItemId: string | undefined;
    isAddDrugDialogVisible?: MedicationsEntity;
    drugsLabTestsStatusMap: DrugsLabTestsStatusMap;
    medsCopilot: boolean;
};

type DrugsAndLabsArray = {
    [k: string]: ItemType[];
};

function getDrugsAndLabsFromOptions(kind: Kind, options: Options): DrugsAndLabsArray {
    let drugs: ItemType[] = [];
    let labs: ItemType[] = [];
    if (kind === MEDICINES_KIND) {
        options.forEach((item) => {
            const { options } = item;
            drugs = drugs.concat(options);
        });
    } else {
        options.forEach((item) => {
            const { options } = item;
            labs = labs.concat(options);
        });
    }

    return { drugs, labs };
}

class PrescribeAutoSuggestV2 extends PureComponent<Props, State> {
    fetchDebounce: (input: string) => void;
    elId = `${this.props.kind}-suggest`;
    ref: Select<ItemType, true> | null = null;

    state: State = {
        isMenuOpen: false,
        options: [],
        focussedItemId: undefined,
        inputValue: '',
        drugsLabTestsStatusMap: {},
        medsCopilot: false,
    };

    constructor(props: Props) {
        super(props);

        this.fetchDebounce = debounce(this.loadOptions, 300);

        if (this.props.kind === 'Medicines') {
            getItemFromMisc<GenieSettings>(getMActiveId() || '', 'settings').then(
                ({ medsCopilot }) => {
                    this.setState({ medsCopilot });
                },
            );
        }
    }

    getNextMedicinesSuggestions = async (timeout: number = 1000): Promise<Drug[]> => {
        const { prescription } = this.props;

        if (!prescription) {
            return [];
        }

        try {
            const { drugs } = await predictNextMedicines(
                {
                    prid: prescription?.id,
                    current_rx: {
                        patient: prescription.patient,
                        date: prescription.date.toISOString(),
                        medications: prescription.medications,
                        symptoms: prescription.symptoms,
                        diagnosis: prescription.diagnosis,
                    },
                },
                timeout,
            );

            return drugs || [];
        } catch (e) {
            console.error(e);
            track('ERROR', {
                on: 'getNextMedicinesSuggestions',
                what: 'Api failed',
            });
        }

        return [];
    };

    /**
     * @todo clean up
     * move to elements terminology, see sym dia AS v2
     */
    setOptionsWhenInputValueIsEmpty = async (timeout: number): Promise<void> => {
        const { medsCopilot } = this.state;

        const { kind, elements, suggestedElements, hideVendorPackages } = this.props;
        let { doctorPackages = [] } = this.props;
        const type =
            kind === MEDICINES_KIND
                ? MEDICATIONS
                : kind === INJECTIONS_KIND
                ? INJECTIONS
                : LAB_TESTS;
        const gElements = PadGenieAutoSuggestFuseManager.getOriginalListForType(type);
        const coPilotOptions: Drug[] | undefined = medsCopilot
            ? await this.getNextMedicinesSuggestions()
            : undefined;

        if (!gElements && !medsCopilot) return;

        setTimeout(() => {
            let mainOptions: ItemType[] = [];
            if (medsCopilot && coPilotOptions?.length) {
                mainOptions = coPilotOptions
                    .filter((e) => !elements.find((t) => t.id === e.id))
                    .map((t, index) => ({
                        ...t,

                        kind: kind,
                        track: {
                            label: AS_EMPTY,
                            index: index,
                            source: COPILOT,
                        },
                    }));
            } else if (gElements) {
                mainOptions = gElements
                    .filter((e) => !elements.find((t) => t.id === e.id))
                    .map((t, index) => ({
                        ...t,

                        kind: kind,
                        track: {
                            label: AS_EMPTY,
                            index: index,
                            source: GENIE,
                        },
                    }));
            }

            const suggestedOptions: ItemType[] = suggestedElements
                .filter((e) => {
                    if (elements.find((t) => t.id === e.id)) {
                        return false;
                    }
                    const genieIndex = mainOptions.findIndex((t) => t.id === e.id);
                    if (genieIndex > -1) {
                        mainOptions.unshift(mainOptions.splice(genieIndex, 1)[0]);
                        return false;
                    }

                    return true;
                })
                .map((t, index) => ({
                    ...t,

                    kind: kind,
                    track: {
                        label: AS_EMPTY,
                        index: index,
                        source: SUGGESTED,
                    },
                }));

            if (kind === LAB_TESTS_KIND) {
                doctorPackages = doctorPackages.filter((i) => {
                    i.kind = 'Lab Tests';
                    i.track = {
                        label: AS_EMPTY,
                        index: 0,
                        source: API_SEARCH,
                    };
                    return (
                        !mainOptions.find((j) => j.id === i.id) &&
                        !elements.find((j) => j.id === i.id)
                    );
                });
                mainOptions = [...doctorPackages, ...mainOptions];
                mainOptions = getTopSuggestionsForLabs(mainOptions);
            } else mainOptions.splice(7);

            suggestedOptions.splice(7);

            const options = [] as { label: LIST_LABEL_TYPE; options: ItemType[] }[];
            if (mainOptions.length > 0) {
                if (kind === LAB_TESTS_KIND) {
                    const { tests, packages } = getTestsAndPackagesFromOptions(mainOptions);

                    if (tests.length) {
                        options.push({
                            label: AS_OPTIONS_TESTS_AND_PANELS,
                            options: tests.map((t) => ({
                                ...t,
                                track: { ...t.track, columnIndex: options.length },
                            })),
                        });
                    }
                    if (packages.length && !hideVendorPackages) {
                        options.push({
                            label: AS_OPTIONS_BOOKABLE_PACKAGES,
                            options: packages.map((t) => ({
                                ...t,
                                track: { ...t.track, columnIndex: options.length },
                            })),
                        });
                    }
                } else {
                    options.push({
                        label: AS_OPTIONS_FREQUENCTLY,
                        options: mainOptions.map((t) => ({
                            ...t,
                            track: { ...t.track, columnIndex: options.length },
                        })),
                    });
                }
            }
            if (suggestedOptions.length > 0) {
                options.push({
                    label: AS_OPTIONS_SUGGESTED,
                    options: suggestedOptions.map((t) => ({
                        ...t,
                        track: { ...t.track, columnIndex: options.length },
                    })),
                });
            }
            this.setState({
                options: options,
                focussedItemId:
                    mainOptions.length > 0
                        ? mainOptions[0].id
                        : suggestedOptions.length > 0
                        ? suggestedOptions[0].id
                        : undefined,
            });

            if (kind === MEDICINES_KIND) {
                const drugIds = options[0]?.options.map((i) => i.id) || [];
                if (drugIds.length) {
                    fetchDrugsByIds(drugIds).then((response) => {
                        if (this.state.inputValue) {
                            // if input value has changed, do not update state
                            return;
                        }
                        const drugs = options[0]?.options.map((i) => ({
                            ...i,
                            ...(response[i.id] || {}),
                            generic_id: response[i.id]?.generic_id || (i as GMedication).generic_id,
                            generic_name:
                                response[i.id]?.generic_name || (i as GMedication).generic_name,
                            name: (i as GMedication).actual_name
                                ? i.name
                                : response[i.id]?.name || i.name,
                        }));
                        this.setState({
                            options: [
                                {
                                    label: AS_OPTIONS_FREQUENCTLY,
                                    options: drugs,
                                },
                            ],
                        });
                    });
                }
            }

            const { drugs, labs } = getDrugsAndLabsFromOptions(kind, options);

            fetchDrugsLabTestsStatus(drugs, labs).then((response) => {
                const { drugsLabTestsStatusMap } = this.state;
                this.setState({
                    drugsLabTestsStatusMap: { ...drugsLabTestsStatusMap, ...response },
                });
            });
        }, timeout);
    };

    onSelectFiltered = (
        selectedOption: MedicationsEntity | LabTestsEntityOrProceduresEntity | InjectionsEntity,
        config: GenieMemoryItem[] | undefined,
    ): void => {
        const { onSelect } = this.props;
        const filteredSelected = filterSuggestion(config, selectedOption);
        onSelect(filteredSelected);
    };

    onSelect = (selectedValues: OptionsType<ItemType>): void => {
        const { kind, isForHXNGFlow } = this.props;
        const { drugsLabTestsStatusMap } = this.state;

        if (selectedValues.length === 0) {
            return;
        }

        let option = selectedValues[0];
        const newStatus = drugsLabTestsStatusMap[option.id];
        if (newStatus && kind === LAB_TESTS_KIND) {
            option = {
                ...option,
                ...newStatus,
            };
        }
        const { genieMemoryConfig = DEFAULT_GENIE_MEMORY_CONFIG } = this.props;
        const { medications, injections, labTests } = genieMemoryConfig;

        if (option.kind === MEDICINES_KIND) {
            if (option.track?.source === GENIE) {
                const selectedOption: MedicationsEntity = {
                    ...option,
                    frequency: {
                        type: 'custom',
                        custom: convertToNewField(
                            'frequency' in option ? option.frequency : undefined,
                        ),
                    },
                    // clean up things
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    dosage: undefined,
                    from: undefined,
                    label: undefined,
                    track: option.track,
                };

                if (
                    option.id.startsWith(LOCAL_ID_PREFIX) &&
                    ((!selectedOption.form && !selectedOption.dosage_form) ||
                        !selectedOption.generic_name) &&
                    !selectedOption.is_otc &&
                    !selectedOption.med_cat
                ) {
                    this.setState({ isAddDrugDialogVisible: selectedOption });
                    this.postSelectDeets();
                    return;
                }

                this.onSelectFiltered(selectedOption, medications);
            } else {
                const selectedOption = {
                    ...apiDrugToMedicationEntity(option as MatchingDrug, isForHXNGFlow),
                    track: option.track,
                };

                if (option.id.startsWith(LOCAL_ID_PREFIX)) {
                    this.setState({ isAddDrugDialogVisible: selectedOption });
                    this.postSelectDeets();
                    return;
                }
                this.onSelectFiltered(selectedOption, medications);
            }
        } else if (option.kind === INJECTIONS_KIND) {
            const selectedOption = {
                ...option,
                df_id: (option as MatchingDrug).dosage?.df_id,
                dosage_form: (option as MatchingDrug).dosage?.dosage_form,
            };
            this.onSelectFiltered(selectedOption, injections);
        } else {
            // TODO
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.onSelectFiltered(option, labTests);
            this.setOptionsWhenInputValueIsEmpty(0);
        }

        this.sendPSSelectEvent(option);
        this.postSelectDeets();
    };

    addDrug = (drug?: MedicationsEntity, matchingDrugs?: CodedMedications[]): void => {
        if (drug) {
            this.props.onSelect(drug);
            this.sendPSSelectEvent({ ...drug, kind: this.props.kind }, matchingDrugs);
        }
        this.setState({ isAddDrugDialogVisible: undefined });
    };

    onEditDrug = (): void => {
        if (!this.state.isAddDrugDialogVisible) {
            return;
        }

        const inputValue = this.state.isAddDrugDialogVisible?.name;

        this.setState(
            {
                inputValue,
                isAddDrugDialogVisible: undefined,
                isMenuOpen: true,
            },
            () => {
                this.loadOptions(inputValue);
                setTimeout(() => {
                    this.adjustScroll();
                    const { kind } = this.props;
                    const t = document.getElementById(
                        `${PRESCRIBE_V2_AS_INPUT}-${kind}`,
                    ) as HTMLInputElement;
                    t && t.focus();
                }, 200);
            },
        );
    };

    onInputChange = (newValue: string): void => {
        const { inputValue } = this.state;
        if (inputValue === newValue) return;
        this.setState({
            inputValue: newValue,
        });

        this.fetchDebounce(newValue);
    };

    scrollIntoView = (id: string): void => {
        try {
            const myContainer = document.getElementById(id) as HTMLDivElement;
            if (myContainer) {
                myContainer.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'nearest',
                });
            }
        } catch (e) {
            console.error(e);
        }
    };

    setFocus = (newId: string | undefined): void => {
        this.setState({
            focussedItemId: newId,
        });
    };

    onKeyDown = (event: React.KeyboardEvent<HTMLElement>): void => {
        if ((event.key === ' ' || event.keyCode === 32) && !this.state.inputValue) {
            event.preventDefault();
            return;
        }

        const { focussedItemId, options, isMenuOpen } = this.state;

        if (event.key === 'Escape') {
            this.ref?.blur();
            this.setState({
                isMenuOpen: false,
            });
            event.stopPropagation();
            event.preventDefault();
            return;
        }

        handleKeyDownEvent<ItemType>({
            event,
            focussedItemId,
            leftList: options && options.length > 0 ? options[0].options : [],
            rightList: options && options.length > 1 ? options[1].options : [],
            setFocus: this.setFocus,
            isMenuOpen,
            onSelect: this.onSelect,
            scrollIntoView: this.scrollIntoView,
        });
    };

    getIsGenieDisabled = (): boolean => {
        const { genieMemoryConfig, kind } = this.props;
        const { isMedicationsEnabled, isInjectionsEnabled, isLabTestEnabled } =
            genieMemoryConfig || {};
        const isGenieDisabled =
            (!isMedicationsEnabled && kind === MEDICINES_KIND) ||
            (!isInjectionsEnabled && kind === INJECTIONS_KIND) ||
            (!isLabTestEnabled && kind === LAB_TESTS_KIND);
        return isGenieDisabled;
    };

    onInputFocus = (): void => {
        const { inputValue } = this.state;
        if (!inputValue) this.setOptionsWhenInputValueIsEmpty(0);

        this.adjustScroll();
    };

    loadOptions = async (inputValue: string): Promise<void> => {
        const { hideVendorPackages, hideCustomEntity, isForHXNGFlow } = this.props;
        if (!inputValue) {
            const isGenieDisabled = this.getIsGenieDisabled();
            if (isGenieDisabled) {
                this.setState({
                    options: [],
                });
            } else this.setOptionsWhenInputValueIsEmpty(0);
            return;
        }

        const { kind, elements } = this.props;
        const gMedFuse = PadGenieAutoSuggestFuseManager.getFuseForType('medications');
        const gMedications = (isIDristi()
            ? []
            : gMedFuse
            ? gMedFuse.search(inputValue).map((option) => option.item)
            : []) as GMedication[];
        const gLabTestsFuse = PadGenieAutoSuggestFuseManager.getFuseForType('labTests');
        const gLabTests = gLabTestsFuse
            ? gLabTestsFuse.search(inputValue).map((option) => option.item)
            : [];
        const gInjectionFuse = PadGenieAutoSuggestFuseManager.getFuseForType('injections');
        const gInjections = gInjectionFuse
            ? gInjectionFuse.search(inputValue).map((option) => option.item)
            : [];

        let allDrugs: ItemType[] = gMedications
            .filter((e) => !elements.find((t) => t.id === e.id))
            .map((gm, index) => ({
                ...gm,
                dosage: {
                    df_id: gm.df_id,
                    dosage_form: gm.form || gm.dosage_form,
                },

                kind: MEDICINES_KIND,
                track: {
                    label: AS_SEARCH,
                    index: index,
                    source: GENIE,
                },
            }));

        let labTestsOptions: ItemType[] = gLabTests
            .filter((e) => !elements.find((t) => t.id === e.id))
            .map((gm, index) => ({
                ...gm,

                kind: LAB_TESTS_KIND,
                track: {
                    label: AS_SEARCH,
                    index: index,
                    source: GENIE,
                },
            }));

        const injectionOptions: ItemType[] = gInjections
            .filter((e) => !elements.find((t) => t.id === e.id))
            .map((gm, index) => ({
                ...gm,

                kind: INJECTIONS_KIND,
                track: {
                    label: AS_SEARCH,
                    index: index,
                    source: GENIE,
                },
            }));

        let options: { label: LIST_LABEL_TYPE; options: ItemType[] }[] = [];

        allDrugs.splice(7);
        labTestsOptions = getTopSuggestionsForLabs(labTestsOptions);
        injectionOptions.splice(7);

        if (kind === MEDICINES_KIND) {
            options = [
                {
                    label: AS_OPTIONS_SEARCH,
                    options: allDrugs,
                },
            ];
        } else if (kind === LAB_TESTS_KIND) {
            const { tests, packages } = getTestsAndPackagesFromOptions(labTestsOptions);

            if (tests.length) {
                options.push({
                    label: AS_OPTIONS_TESTS_AND_PANELS,
                    options: tests,
                });
            }
            if (packages.length && !hideVendorPackages) {
                options.push({
                    label: AS_OPTIONS_BOOKABLE_PACKAGES,
                    options: packages,
                });
            }
        }

        this.setState({
            options,
            isMenuOpen: options[0]?.options?.length > 0,
            focussedItemId:
                options[0]?.options?.length > 0 ? options[0]?.options[0]?.id : undefined,
        });

        if (kind === MEDICINES_KIND) {
            const drugIds = options[0]?.options.map((i) => i.id) || [];
            if (drugIds.length) {
                fetchDrugsByIds(drugIds).then((response) => {
                    if (this.state.inputValue !== inputValue) {
                        // if input value has changed, do not update state
                        return;
                    }
                    const drugs = this.state.options[0]?.options.map((i) => ({
                        ...i,
                        ...(response[i.id] || {}),
                        generic_id: response[i.id]?.generic_id || (i as GMedication).generic_id,
                        generic_name:
                            response[i.id]?.generic_name || (i as GMedication).generic_name,
                        name: (i as GMedication).actual_name
                            ? i.name
                            : response[i.id]?.name || i.name,
                    }));
                    this.setState({
                        options: [
                            {
                                label: AS_OPTIONS_SEARCH,
                                options: drugs,
                            },
                        ],
                    });
                });
            }
        }

        const { drugs, labs } = getDrugsAndLabsFromOptions(kind, options);
        fetchDrugsLabTestsStatus(drugs, labs).then((response) => {
            const { drugsLabTestsStatusMap } = this.state;
            this.setState({
                drugsLabTestsStatusMap: { ...drugsLabTestsStatusMap, ...response },
            });
        });

        let response;
        const { medTypeIds = [], selectedClinic, prescription } = this.props;
        try {
            const cat = medTypeIds.join(',');
            const isHideCustomEntity =
                hideCustomEntity ||
                isRXMonetisationT3() ||
                (kind === LAB_TESTS_KIND && isRXMonetisationT4());
            const sType =
                kind === MEDICINES_KIND || kind === INJECTIONS_KIND
                    ? S_TYPE.DRUG
                    : kind === LAB_TESTS_KIND
                    ? S_TYPE.LAB
                    : '';
            response = await fetchMatchingDrugsLabTests(
                inputValue,
                cat,
                undefined,
                isHideCustomEntity,
                selectedClinic,
                isForHXNGFlow,
                sType,
                kind === INJECTIONS_KIND ? true : undefined,
                kind,
                prescription?.clinic.partner_clinic_id,
            );
        } catch (e) {}

        let medRes =
            kind === MEDICINES_KIND
                ? response?.drugs?.filter((e) => !elements.find((t) => t.id === e.id))
                : undefined;
        if (isForHXNGFlow) medRes = medRes?.filter((i) => i.hxng_status === 'available');

        // genie drugs might have been updated in state by the fetchDrugsByIds call
        allDrugs = this.state.options[0]?.options ? [...this.state.options[0].options] : allDrugs;
        if (medRes) {
            medRes.forEach((i) => {
                if (!allDrugs.find((s) => s.id === i.id)) {
                    allDrugs.push({
                        ...i,
                        kind: MEDICINES_KIND,
                        track: {
                            label: AS_SEARCH,
                            index: allDrugs.length,
                            source: API_SEARCH,
                        },
                    });
                }
            });
        }

        allDrugs.splice(7);

        if (
            !allDrugs[allDrugs.length - 1]?.id.startsWith(LOCAL_ID_PREFIX) &&
            medRes &&
            medRes.length > 0 &&
            medRes[medRes.length - 1].id.startsWith(LOCAL_ID_PREFIX) &&
            !isRXMonetisationT3()
        ) {
            allDrugs.push({
                ...medRes[medRes.length - 1],
                kind: MEDICINES_KIND,
                track: {
                    label: AS_SEARCH,
                    index: allDrugs.length,
                    source: API_SEARCH,
                },
            });
        } else {
            if (!response && !isRXMonetisationT3()) {
                // there has been an error while calling API
                const isDrugMatchFound = allDrugs.find(
                    (i) => i.name.toLowerCase() === inputValue.trim().toLowerCase(),
                );

                if (!isDrugMatchFound && inputValue) {
                    allDrugs.push({
                        id: `${LOCAL_ID_PREFIX}d-${hashCode(inputValue.trim())}`,
                        name: inputValue,
                        manufacturer_name: '',
                        product_type: '',
                        generic_name: '',
                        soa: true,
                        kind: MEDICINES_KIND,
                        track: {
                            label: AS_SEARCH,
                            index: allDrugs.length,
                            source: CUSTOM,
                        },
                    });
                }
            }
        }

        let labRes = response?.lab_tests?.filter((e) => !elements.find((t) => t.id === e.id));
        if (isForHXNGFlow) labRes = labRes?.filter((i) => i.book);

        if (labRes) {
            labRes.forEach((i) => {
                if (!labTestsOptions.find((s) => s.id === i.id)) {
                    labTestsOptions.push({
                        ...i,
                        kind: LAB_TESTS_KIND,
                        track: {
                            label: AS_SEARCH,
                            index: labTestsOptions.length,
                            source: API_SEARCH,
                        },
                    });
                }
            });
        }

        labTestsOptions = getTopSuggestionsForLabs(labTestsOptions);

        if (
            !labTestsOptions[labTestsOptions.length - 1]?.id.startsWith(LOCAL_ID_PREFIX) &&
            labRes &&
            labRes.length > 0 &&
            labRes[labRes.length - 1].id.startsWith(LOCAL_ID_PREFIX) &&
            !isRXMonetisationT3() &&
            !isRXMonetisationT4()
        ) {
            labTestsOptions.push({
                ...labRes[labRes.length - 1],
                kind: LAB_TESTS_KIND,
                track: {
                    label: AS_SEARCH,
                    index: labTestsOptions.length,
                    source: API_SEARCH,
                },
            });
        } else {
            if (!response && !isRXMonetisationT3() && !isRXMonetisationT4()) {
                // there has been an error while calling API
                const isLabTestMatchFound = labTestsOptions.find(
                    (i) => i.name.toLowerCase() === inputValue.trim().toLowerCase(),
                );

                if (!isLabTestMatchFound && inputValue) {
                    labTestsOptions.push({
                        id: `${LOCAL_ID_PREFIX}lt-${hashCode(inputValue.trim())}`,
                        name: inputValue,
                        kind: LAB_TESTS_KIND,
                        track: {
                            label: AS_SEARCH,
                            index: labTestsOptions.length,
                            source: CUSTOM,
                        },
                    });
                }
            }
        }

        const injectionResponse =
            kind === INJECTIONS_KIND
                ? response?.drugs?.filter((e) => !elements.find((t) => t.id === e.id))
                : undefined;

        if (injectionResponse) {
            injectionResponse.forEach((i) => {
                if (!injectionOptions.find((s) => s.id === i.id)) {
                    injectionOptions.push({
                        ...i,
                        kind: INJECTIONS_KIND,
                        track: {
                            label: AS_SEARCH,
                            index: injectionOptions.length,
                            source: API_SEARCH,
                        },
                    });
                }
            });
        }

        if (kind === MEDICINES_KIND) {
            options = [
                {
                    label: AS_OPTIONS_SEARCH,
                    options: allDrugs,
                },
            ];
        } else if (kind === LAB_TESTS_KIND) {
            const { tests, packages } = getTestsAndPackagesFromOptions(labTestsOptions);

            options = [];

            if (tests.length) {
                options.push({
                    label: AS_OPTIONS_TESTS_AND_PANELS,
                    options: tests,
                });
            }
            if (packages.length && !hideVendorPackages) {
                options.push({
                    label: AS_OPTIONS_BOOKABLE_PACKAGES,
                    options: packages,
                });
            }
        } else if (kind === INJECTIONS_KIND) {
            if (injectionOptions && injectionOptions.length) {
                options = [
                    {
                        label: AS_OPTIONS_SEARCH,
                        options: injectionOptions,
                    },
                ];
            }
        }

        track('AS_SEARCH', {
            searchString: this.state.inputValue,
            kind:
                kind === MEDICINES_KIND
                    ? MEDICINES_KIND
                    : kind === INJECTIONS_KIND
                    ? INJECTIONS_KIND
                    : LAB_TESTS_KIND,
            total: (kind === MEDICINES_KIND
                ? allDrugs
                : kind === INJECTIONS_KIND
                ? injectionOptions
                : labTestsOptions
            )
                ?.map((option) => option.id)
                ?.toString(),
        });

        this.setState({
            options,
            isMenuOpen: options[0]?.options?.length > 0,
            focussedItemId:
                options[0]?.options?.length > 0 ? options[0]?.options[0]?.id : undefined,
        });
    };

    setMenuOnOff = (value: boolean): void => {
        this.setState({ isMenuOpen: value });
    };

    private readonly filterOption = () => true;

    private readonly getOptionValue = (e: ItemType) => e.name;

    private postSelectDeets() {
        this.setState({
            isMenuOpen: false,
            inputValue: '',
            focussedItemId: undefined,
        });

        this.adjustScroll();
        this.ref?.blur();
    }

    private sendPSSelectEvent(option: ItemType, matchingDrugs?: CodedMedications[]) {
        track('PAD_AS_SELECT', {
            eid: option.id,
            searchString: this.state.inputValue,
            selectedString: option.name,
            kind: option.kind,
            label: option.track?.label,
            source: option.track?.source,
            index: option.track?.index?.toString(),
            total:
                (option.track?.columnIndex !== undefined &&
                    this.state.options[option.track?.columnIndex].options.length.toString()) ||
                undefined,
            matchingDrugs: JSON.stringify(matchingDrugs),
        });
    }

    private adjustScroll() {
        if (this.props.hideVendorPackages || this.props.disableAutoScrollOnSelect) return;
        const el = document.getElementById(this.elId) as HTMLDivElement;
        const con = document.getElementById(PAD_V2_CONTAINER) as HTMLDivElement;

        if (el && con) {
            const diff = 80 - (con.offsetHeight - (340 + el.getBoundingClientRect().y));
            diff > 0 &&
                con.scrollBy({
                    top: diff,
                    behavior: 'smooth',
                });
        }
    }

    closeAddDrugContainer = (): void => {
        this.setState({ isAddDrugDialogVisible: undefined });
    };

    render(): ReactNode {
        const {
            inputValue,
            isMenuOpen,
            options,
            focussedItemId,
            isAddDrugDialogVisible,
            drugsLabTestsStatusMap,
        } = this.state;
        const {
            kind,
            suggestedElements,
            drugAllergies,
            isForAddMedicines,
            hideVendorPackages,
            className,
        } = this.props;

        if (isAddDrugDialogVisible && isForAddMedicines) {
            this.addDrug(isAddDrugDialogVisible);
        } else if (isAddDrugDialogVisible) {
            return (
                <div className="w-full shadow-800 border border-darwin-neutral-200">
                    <AddDrugDialogWrapper
                        drug={isAddDrugDialogVisible}
                        onSelect={this.addDrug}
                        _source="AS"
                        onEdit={this.onEditDrug}
                    />
                </div>
            );
        }

        return (
            <div
                className={`w-full rounded-8 border border-neutral-7 px-12 flex items-center row-input-container space-x-10 ${
                    className ? className : ''
                }`}
            >
                <Select
                    ref={(ref) => (this.ref = ref)}
                    id={this.elId}
                    value={[]}
                    isMulti
                    isClearable={false}
                    placeholder={
                        kind === MEDICINES_KIND
                            ? 'Start typing Medicines'
                            : kind === INJECTIONS_KIND
                            ? 'Start typing an Injection'
                            : 'Start typing Lab test / Radiology'
                    }
                    components={customComponents}
                    styles={customStyles}
                    getOptionValue={this.getOptionValue}
                    onChange={this.onSelect}
                    onFocus={this.onInputFocus}
                    onInputChange={this.onInputChange}
                    options={options}
                    drugsLabTestsStatusMap={drugsLabTestsStatusMap}
                    inputValue={inputValue}
                    tabSelectsValue={false}
                    filterOption={this.filterOption}
                    menuIsOpen={isMenuOpen}
                    setMenuOnOff={this.setMenuOnOff}
                    onKeyDown={this.onKeyDown}
                    focussedItemId={focussedItemId}
                    setFocussedId={this.setFocus}
                    kind={kind}
                    drugAllergies={drugAllergies}
                    optionsType={
                        !inputValue
                            ? suggestedElements.length > 0
                                ? 'associated'
                                : 'frequently'
                            : undefined
                    }
                    autoFocus={hideVendorPackages}
                />
            </div>
        );
    }
}

export default connect((state: GlobalState, props: CompProps) => ({
    elements: Array.isArray(props.prescriptionElements)
        ? props.prescriptionElements
        : (props.kind === MEDICINES_KIND ? getMedications : getLabTests)(state),
    drugAllergies: getDrugAllergies(state),
    medTypeIds: getMedTypeIds(state),
    suggestedElements:
        props.kind === MEDICINES_KIND
            ? [
                  ...getSuggestedDrugs(state),
                  ...getComplimentaryDrug(state).reduce((all, item) => {
                      item.complimentary &&
                          all.push(
                              ...item.complimentary.reduce((a, d) => {
                                  d.drugs && a.push(...d.drugs);
                                  return a;
                              }, [] as E[]),
                          );
                      return all;
                  }, [] as E[]),
              ]
            : getSuggestedLabTests(state),
    doctorPackages: getDoctorPackages(state),
    selectedClinic: getRxClinicId(state),
    disableAutoScrollOnSelect: getGenieSetting(state, 'disableAutoScrollOnSelect') as boolean,
    prescription: getPrescription(state),
    genieMemoryConfig: (() => {
        const config = getGenieSetting(state, 'genieMemoryConfig');
        return typeof config === 'object' && config !== null && !Array.isArray(config)
            ? (config as GenieMemoryConfig)
            : undefined;
    })(),
}))(PrescribeAutoSuggestV2);

export function apiDrugToMedicationEntity(
    option: MatchingDrug,
    useDefault?: boolean,
): MedicationsEntity {
    return {
        id: option.id,
        name: option.name,
        form: '',
        dosage_form: option.dosage?.dosage_form || '',
        dose: useDefault
            ? {
                  value: '1',
                  unit: 'unit',
                  id: 'id',
              }
            : {
                  value: option.dosage?.unit_val,
                  unit: option.dosage?.unit_name,
                  id: option.dosage?.unit_id,
              },
        df_id: option.dosage?.df_id,
        generic_id: option.generic_id,
        generic_name: option.generic_name,
        frequency: useDefault
            ? {
                  type: 'custom',
                  custom: '1',
              }
            : {
                  type: 'custom',
                  custom: option.dosage?.dosage,
              },
        timing: option.dosage?.food
            ? DRUG_DOSAGE_DURATION_TIMING_ENUM[option.dosage?.food] || ''
            : '',
        duration: {
            value: option.dosage?.days || undefined,
            unit: option.dosage?.days ? 'Days' : undefined,
            custom: option.dosage?.days
                ? `${option.dosage?.days || ''} ${getUnit('Days', option.dosage?.days)}`
                : undefined,
        },
        product_type: option.product_type,
        soa: option.soa || false,
        manufacturer_name: option.manufacturer_name,
        inventory_status: option.inventory_status,
    };
}

export const convertToNewField = (frequency?: Frequency): string => {
    if (!frequency) return '';
    return frequency.type === 'custom'
        ? frequency.custom || ''
        : frequency.type === 'code'
        ? frequency.code || ''
        : frequency.type === 'SOS'
        ? 'SOS'
        : frequency.type === 'toContinue'
        ? 'To continue'
        : frequency.type === 'onceEvery'
        ? `Once every ${frequency.onceEvery?.value} ${frequency.onceEvery?.unit}`
        : '';
};
